import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, FileText, Database, Layers, Eye, Share2, Send,
  Lock, Clock, UserCheck, Cookie, Baby, AlertOctagon, ExternalLink,
  History, Mail, Phone, MapPin, User, ChevronRight
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";

const SECTIONS = [
  { id: "scope", title: "1. Scope", icon: Layers },
  { id: "information-collected", title: "2. Information We May Collect", icon: Database },
  { id: "automatic-collection", title: "3. Information Collected Automatically", icon: Eye },
  { id: "purpose", title: "4. Purpose of Collection", icon: FileText },
  { id: "consent", title: "5. Consent & Lawful Processing", icon: UserCheck },
  { id: "sharing", title: "6. Sharing of Information", icon: Share2 },
  { id: "submissions", title: "7. Government & Professional Submissions", icon: Send },
  { id: "security", title: "8. Data Security", icon: Lock },
  { id: "retention", title: "9. Data Retention", icon: Clock },
  { id: "rights", title: "10. Your Rights", icon: ShieldCheck },
  { id: "cookies", title: "11. Cookies", icon: Cookie },
  { id: "children", title: "12. Children's Data", icon: Baby },
  { id: "breaches", title: "13. Data Breaches", icon: AlertOctagon },
  { id: "third-party", title: "14. Third-Party Websites", icon: ExternalLink },
  { id: "changes", title: "15. Changes to This Policy", icon: History },
  { id: "contact", title: "16. Contact & Grievance", icon: Mail },
];

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("scope");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy | Biz360 Compliance"
        description="Privacy Policy explaining data collection, protection, client rights, data security, and grievance redressal at Biz360 Compliance."
        canonical="/privacy-policy"
        breadcrumbs={[{ label: "Privacy Policy", href: "/privacy-policy" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="privacy-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Privacy Policy</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Privacy & Data Protection
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  PRIVACY POLICY
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Biz360 Compliance (&ldquo;Biz360&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;) respects the privacy of individuals whose personal information is collected or processed through our website, client portal, applications, communication channels and services.
                  </p>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">
                    This Privacy Policy explains what information we collect, why we collect it, how we use it, how we protect it and the choices available to you.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Privacy Articles Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. Scope */}
              <article id="scope" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Layers className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. Scope</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  This Policy applies to information collected through:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Our website;",
                    "Contact and enquiry forms;",
                    "Client portal;",
                    "Customer accounts;",
                    "Service engagements;",
                    "Email and other official communication;",
                    "Documents submitted by clients;",
                    "Payment and billing interactions; and",
                    "Other authorised digital or offline interactions relating to our services."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 2. Information We May Collect */}
              <article id="information-collected" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Database className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. Information We May Collect</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Depending on the service, we may collect:
                </p>

                {/* Personal Information */}
                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-navy-900">Personal Information</h3>
                  <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                    {[
                      "Name",
                      "Mobile number",
                      "Email address",
                      "Address",
                      "Date of birth, where required",
                      "Identification information",
                      "PAN/Aadhaar-related information where legitimately required",
                      "Professional or business details"
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Business Information */}
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-navy-900">Business Information</h3>
                  <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                    {[
                      "Business name",
                      "Entity type",
                      "GSTIN",
                      "PAN",
                      "CIN/LLPIN",
                      "Udyam details",
                      "Business address",
                      "Financial information",
                      "Accounting information",
                      "Tax and compliance information"
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Service Documents */}
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-navy-900">Service Documents</h3>
                  <p className="mt-1 text-xs text-slate-500">Depending on the engagement, documents may include:</p>
                  <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                    {[
                      "Financial statements",
                      "Bank statements",
                      "GST records",
                      "Income Tax records",
                      "TDS information",
                      "Registration documents",
                      "Government forms",
                      "Contracts and supporting documents"
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>

              {/* 3. Information Collected Automatically */}
              <article id="automatic-collection" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Eye className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Information Collected Automatically</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  When you use our website or digital systems, certain technical information may be collected, such as:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "IP address;",
                    "Browser type;",
                    "Device information;",
                    "Operating system;",
                    "Login activity;",
                    "Date and time of access;",
                    "Pages or features accessed; and",
                    "Security and diagnostic information."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 4. Purpose of Collection */}
              <article id="purpose" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FileText className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Purpose of Collection</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Information may be processed for:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Responding to enquiries;",
                    "Providing requested services;",
                    "Preparing applications and documents;",
                    "Accounting and billing;",
                    "Client communication;",
                    "Compliance management;",
                    "Maintaining service records;",
                    "Processing payments;",
                    "Managing client accounts;",
                    "Providing customer support;",
                    "Preventing fraud and misuse;",
                    "Improving our services;",
                    "Maintaining security;",
                    "Meeting legal and regulatory obligations; and",
                    "Other purposes communicated at the time of collection."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 5. Consent & Lawful Processing */}
              <article id="consent" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <UserCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Consent & Lawful Processing</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where consent is required, we will seek consent through an appropriate mechanism.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Where permitted by applicable law, information may also be processed where necessary for contractual, legal, security or other legally recognised purposes.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Where consent is the basis for processing, applicable rights relating to withdrawal of consent will be respected, subject to lawful exceptions and consequences.
                </p>
              </article>

              {/* 6. Sharing of Information */}
              <article id="sharing" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Share2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Sharing of Information</h2>
                </div>
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3.5 text-xs font-bold text-emerald-900">
                  We do not sell personal information as a business practice.
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Information may be shared where reasonably necessary with:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Employees and authorised personnel;",
                    "Professional advisers;",
                    "Chartered Accountants, advocates or other authorised professionals;",
                    "Government departments and regulatory authorities;",
                    "Banks or financial institutions;",
                    "Payment processors;",
                    "Technology and hosting providers;",
                    "Communication/service providers; and",
                    "Other service providers acting on our behalf."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-medium text-slate-500 italic">
                  Information will be shared only to the extent reasonably required for the relevant purpose or as required/permitted by law.
                </p>
              </article>

              {/* 7. Government & Professional Submissions */}
              <article id="submissions" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Send className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">7. Government & Professional Submissions</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where you engage us for registration, taxation, compliance, refund, licensing, funding or other services, relevant information may need to be submitted to government portals, regulatory authorities, banks or authorised professionals.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-700 font-medium">
                  The client authorises such submission to the extent necessary for performing the agreed service, subject to applicable law.
                </p>
              </article>

              {/* 8. Data Security */}
              <article id="security" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Lock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">8. Data Security</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  We use reasonable technical and organisational measures to protect information against unauthorised access, misuse, loss, alteration or disclosure.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Security measures may include:</p>
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Access controls;",
                    "Role-based permissions;",
                    "Authentication mechanisms;",
                    "Encryption where appropriate;",
                    "Secure document storage;",
                    "Monitoring and logging;",
                    "Backup mechanisms; and",
                    "Restricted employee access."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Further information is provided in our{" "}
                  <Link to="/data-security-policy" className="text-navy-900 underline font-bold hover:text-gold-600 transition-colors">
                    Data Security Policy
                  </Link>.
                </p>
              </article>

              {/* 9. Data Retention */}
              <article id="retention" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Clock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">9. Data Retention</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Information will generally be retained only for as long as reasonably necessary for the purpose for which it was collected, contractual requirements, statutory requirements, professional record-keeping, dispute resolution, security or other lawful purposes.
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Further details are provided in our{" "}
                  <Link to="/data-retention-policy" className="text-navy-900 underline font-bold hover:text-gold-600 transition-colors">
                    Data Retention & Deletion Policy
                  </Link>.
                </p>
              </article>

              {/* 10. Your Rights */}
              <article id="rights" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">10. Your Rights</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Subject to applicable law, individuals may have rights relating to their personal information, including rights concerning:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Access to personal data;",
                    "Correction of inaccurate information;",
                    "Withdrawal of consent where consent is the basis of processing;",
                    "Erasure/deletion where legally applicable;",
                    "Grievance redressal; and",
                    "Other rights available under applicable data-protection law."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-medium text-slate-500">
                  Requests may be submitted using the contact details provided below.
                </p>
              </article>

              {/* 11. Cookies */}
              <article id="cookies" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Cookie className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">11. Cookies</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Our website may use cookies or similar technologies for:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Essential website functionality;",
                    "Security;",
                    "Analytics;",
                    "User preferences; and",
                    "Other permitted purposes."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-navy-800 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Please refer to our{" "}
                  <Link to="/cookie-policy" className="text-navy-900 underline font-bold hover:text-gold-600 transition-colors">
                    Cookie Policy
                  </Link>{" "}
                  for further information.
                </p>
              </article>

              {/* 12. Children's Data */}
              <article id="children" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Baby className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">12. Children&apos;s Data</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Our services are primarily intended for businesses and adults.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  We do not knowingly seek to collect personal information from children except where permitted and lawfully required for a specific service.
                </p>
              </article>

              {/* 13. Data Breaches */}
              <article id="breaches" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <AlertOctagon className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">13. Data Breaches</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  If a personal-data breach occurs, Biz360 will take appropriate steps based on the nature and severity of the incident and applicable legal requirements, including required notifications where applicable.
                </p>
              </article>

              {/* 14. Third-Party Websites */}
              <article id="third-party" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ExternalLink className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">14. Third-Party Websites</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Our website may contain links to third-party websites.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Their privacy practices are governed by their own policies. Users should review the privacy policy of the relevant third party before submitting information.
                </p>
              </article>

              {/* 15. Changes to This Policy */}
              <article id="changes" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <History className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">15. Changes to This Policy</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  This Privacy Policy may be updated from time to time to reflect changes in law, technology, services or business practices.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  The updated version will be published with a revised &ldquo;Last Updated&rdquo; date.
                </p>
              </article>

              {/* 16. Contact & Grievance */}
              <article id="contact" className="rounded-2xl border border-gold-300/60 bg-gradient-to-br from-white to-gold-50/20 p-6 md:p-8 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gold-600 text-white shadow-sm">
                    <Mail className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">16. Contact & Grievance Redressal</h2>
                </div>
                <p className="mt-3 text-sm text-slate-700">
                  For privacy-related questions, requests or complaints:
                </p>
                <div className="mt-5 space-y-4 rounded-xl border border-navy-900/10 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <User className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Privacy / Grievance Contact: <strong className="text-navy-900">Narayan Bochare</strong></span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <Mail className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Email: <a href="mailto:biz360compliance@gmail.com" className="font-semibold text-navy-900 hover:text-gold-600 transition-colors">biz360compliance@gmail.com</a></span>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <Phone className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Phone: <a href="tel:9067368357" className="font-semibold text-navy-900 hover:text-gold-600 transition-colors">9067368357</a></span>
                  </div>

                  <div className="flex items-start gap-3 text-sm text-slate-700">
                    <MapPin className="h-4 w-4 text-gold-600 shrink-0 mt-0.5" />
                    <span>Address: <strong>Office No. 70, Patil Arcade, Above Kalinga Hotel, Khilare Road, Erandwane, Pune 411004.</strong></span>
                  </div>
                </div>
                <p className="mt-4 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  For our comprehensive grievance and dispute procedures, please refer to our{" "}
                  <Link to="/grievance-redressal-policy" className="text-navy-900 underline font-bold hover:text-gold-600 transition-colors">
                    Grievance Redressal Policy
                  </Link>.
                </p>
                <p className="mt-2 text-xs text-slate-500 italic">
                  We will endeavour to respond to privacy-related requests within the applicable legal or reasonable timeframe.
                </p>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
